import React from 'react';
import { Fingerprint, Leaf, Building2, ArrowRight } from 'lucide-react';

const tiles = [
  {
    icon: Fingerprint,
    pillar: 'Pillar 01',
    title: 'Identity & Compliance Intelligence',
    flagship: 'The CRI Programme',
    blurb: 'Authorisation infrastructure for sovereign-scale identity and compliance.',
  },
  {
    icon: Leaf,
    pillar: 'Pillar 02',
    title: 'Green Compliance & Circular Economy',
    blurb: 'Turning environmental compliance obligations into long-duration revenue.',
  },
  {
    icon: Building2,
    pillar: 'Pillar 03',
    title: 'Sovereign RegTech Solutions (B2G)',
    blurb: 'Unblocking regulatory deadlocks so foreign capital can enter responsibly.',
  },
];

const WhatWeDo = () => {
  const goToServices = () => {
    document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative bg-white py-16 md:py-20 lg:py-24" data-testid="what-we-do-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header strip */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#3eb5a0] font-semibold mb-3">
              What we do
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2d2d2d] leading-tight max-w-2xl">
              Three integrated strategic pillars.
              <br />
              <span className="text-[#3eb5a0]">Built for sovereign scale.</span>
            </h2>
          </div>
          <button
            onClick={goToServices}
            data-testid="what-we-do-explore-btn"
            className="self-start md:self-end inline-flex items-center gap-2 text-sm md:text-base text-[#2d2d2d] font-semibold hover:text-[#3eb5a0] transition-colors duration-300 group"
          >
            Explore all pillars
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>

        {/* Tiles */}
        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {tiles.map((t, i) => {
            const Icon = t.icon;
            return (
              <button
                key={t.pillar}
                onClick={goToServices}
                data-testid={`what-we-do-tile-${i}`}
                className="group text-left relative overflow-hidden rounded-2xl bg-[#1a1a1a] p-6 md:p-7 border border-[#3eb5a0]/15 hover:border-[#3eb5a0] transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl flex flex-col"
              >
                {/* Subtle gradient sheen */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#3eb5a0]/10 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                <div className="relative z-10 flex items-start justify-between mb-6">
                  <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-[#3eb5a0]/10 border border-[#3eb5a0]/30 flex items-center justify-center transition-colors duration-300 group-hover:bg-[#3eb5a0] group-hover:border-[#3eb5a0]">
                    <Icon size={24} className="text-[#3eb5a0] transition-colors duration-300 group-hover:text-[#1a1a1a]" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-medium">
                    {t.pillar}
                  </span>
                </div>

                <h3 className="relative z-10 text-xl md:text-2xl font-semibold text-white leading-snug mb-2">
                  {t.title}
                </h3>
                {t.flagship && (
                  <p
                    data-testid="whatwedo-tile-flagship"
                    className="relative z-10 text-xs md:text-sm text-[#3eb5a0] font-medium tracking-wide mb-3"
                  >
                    Flagship: {t.flagship}
                  </p>
                )}
                <p className="relative z-10 text-sm md:text-[15px] text-white/65 font-light leading-relaxed flex-1" style={{ lineHeight: '1.7' }}>
                  {t.blurb}
                </p>

                <div className="relative z-10 mt-6 pt-5 border-t border-white/10 flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] text-white/50 font-medium">
                    See details
                  </span>
                  <ArrowRight
                    size={18}
                    className="text-[#3eb5a0] transition-transform duration-300 group-hover:translate-x-1"
                  />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatWeDo;
